import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

export interface BreakdownBarListRow {
  key: string;
  label: ReactNode;
  primary: number;
  secondary?: number;
  secondaryLabel?: string;
  /** Second bar segment drawn after `primary` (e.g. cross-domain use). Bar max uses primary + stacked. */
  stacked?: number;
  /** Row becomes a react-router <Link>. */
  href?: string;
  /** Muted styling, no amber zero highlight (e.g. excluded skills). */
  muted?: boolean;
}

interface BreakdownBarListProps {
  rows: BreakdownBarListRow[];
  primaryLabel?: string;
  /** When true, the primary count row text is suffixed with primaryLabel. */
  showPrimaryLabel?: boolean;
}

/**
 * Flat bar list in canonical (given) order — never sorted by count.
 * Bars are sized on the primary count; zero-count rows are amber-highlighted
 * so catalogue gaps stand out.
 */
export function BreakdownBarList({
  rows,
  primaryLabel = 'Activities',
  showPrimaryLabel = false,
}: BreakdownBarListProps) {
  const maxPrimary = Math.max(0, ...rows.map((row) => row.primary + (row.stacked ?? 0)));

  return (
    <div className="space-y-2">
      {rows.map((row) => {
        const total = row.primary + (row.stacked ?? 0);
        const isZero = total === 0 && !row.muted;
        const scale = (n: number) => (maxPrimary > 0 ? (n / maxPrimary) * 100 : 0);
        const tooltip = `${row.primary} ${primaryLabel.toLowerCase()}${
          row.stacked !== undefined ? ` · ${row.stacked} cross-domain` : ''
        }${row.secondary !== undefined ? ` · ${row.secondary} ${row.secondaryLabel ?? ''}`.trimEnd() : ''}`;
        const className = cn(
          'flex items-center gap-3 rounded-pine-input border px-3 py-2',
          isZero ? 'bg-amber-50 border-amber-300' : 'border-pine-line bg-white',
          row.muted && 'opacity-70',
          row.href && !isZero && 'hover:bg-pine-hover-item'
        );
        const body = (
          <>
            <div
              className={cn(
                'min-w-0 flex-1 text-sm',
                isZero ? 'text-amber-800 font-medium' : 'text-pine-ink'
              )}
            >
              {row.label}
            </div>
            <div className="w-40 shrink-0 hidden sm:block">
              {total > 0 && (
                <div className="flex h-2 overflow-hidden rounded-full">
                  <div className="bg-pine-pine" style={{ width: `${scale(row.primary)}%` }} />
                  {row.stacked ? (
                    <div className="bg-pine-l3" style={{ width: `${scale(row.stacked)}%` }} />
                  ) : null}
                </div>
              )}
            </div>
            <div
              className={cn(
                'shrink-0 text-right text-sm tabular-nums',
                isZero ? 'text-amber-800 font-semibold' : 'text-pine-ink'
              )}
            >
              <span className="font-semibold">{row.primary}</span>
              {showPrimaryLabel && (
                <span className={cn('ml-1 text-xs', isZero ? 'text-amber-700' : 'text-pine-ink-soft')}>
                  {primaryLabel.toLowerCase()}
                </span>
              )}
              {row.stacked ? (
                <span className="ml-1 text-xs text-pine-ink-soft">+ {row.stacked}</span>
              ) : null}
              {row.secondary !== undefined && (
                <span className={cn('ml-2 text-xs', isZero ? 'text-amber-700' : 'text-pine-ink-soft')}>
                  {row.secondary} {row.secondaryLabel}
                </span>
              )}
            </div>
          </>
        );

        return row.href ? (
          <Link key={row.key} to={row.href} title={tooltip} className={className}>
            {body}
          </Link>
        ) : (
          <div key={row.key} title={tooltip} className={className}>
            {body}
          </div>
        );
      })}
    </div>
  );
}
